import React, { useEffect, useRef, useState } from 'react'

import { DataGrid, useGridApiRef, } from '@mui/x-data-grid'
import { styled } from '@mui/material/styles'

import notFound from "../../containers/Contacts/Table/NotFoundData"
import CustomPagination from "../CustomPagination/CustomPagination"
import DebounceInput from "../DebouncedSearch/DebouncedSearch"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import { useDispatch, useSelector } from "react-redux"
import { getEmailsReq } from "../../features/emails/emails"
import { extractShortDate } from "../../utils/date"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { MESSAGES_DELIVERY_STATUS } from "../../config/featureFlagKeys"
import DeliveryStatusBadge from "../DeliveryStatusBadge/DeliveryStatusBadge"
import { isPendingDelivery } from "../DeliveryStatusBadge/deliveryStatus"
import { usePendingDeliveryPoll } from "../../hooks/usePendingDeliveryPoll"

// Local styled component removed in favor of global theme overrides

const EmailTableList = ({ userId, setCountMailsTab, countMailsTab, setOpenEmail, activeTab }) => {
  const apiRef = useGridApiRef()
  const { triggerLoading } = useMyContextLoading()
  const dispatch = useDispatch()
  const deliveryOn = useSelector(selectFeatureFlag(MESSAGES_DELIVERY_STATUS))
  const showDelivery = deliveryOn && activeTab === 'sent'
  const [data, setData] = useState([])
  const [offset, setOffset] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const latestRequest = useRef(0)

  let totalPages = activeTab === 'sent' ? countMailsTab?.sentLettersCount : countMailsTab?.inboxLettersCount

  const getEmails = async ({ silent = false } = {}) => {
    if (!silent) triggerLoading(true)

    latestRequest.current += 1
    const requestId = latestRequest.current

    const response = await dispatch(getEmailsReq({
      limit: 10,
      offset: offset * 10,
      userId,
      searchQuery,
      ...(activeTab === 'sent' ? { inbox: false } : { inbox: true })
    }))
    if (silent && (requestId !== latestRequest.current || response?.error || !Array.isArray(response?.payload?.data?.letters))) return

    const transformedData = response?.payload?.data?.letters?.map(item => ({
      id: item?._id,
      createdAt: item?.createdAt,
      subject: item?.subject,
      delivery: item?.delivery
    }))
    setData(transformedData ?? [])
    setCountMailsTab(response?.payload?.data?.totalLettersCount)
    if (!silent) triggerLoading(false)
  }

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: '50px'
    },
    {
      "field": "subject",
      "headerName": "Title",
      renderCell: (params) => {
        return (
          <div className="w-full h-[75px] cursor-pointer flex items-center"
            onClick={() => setOpenEmail(params?.row?.id)} key={params?.row?.id}>
            {params?.row?.subject}
          </div>)
      },
      flex: 1, sortable: false
    },
    {
      "field": "createdAt",
      "headerName": "Date",
      width: 100,
      renderCell: (params) => {
        return (
          <div className="w-full h-[75px] cursor-pointer flex items-center"
            onClick={() => setOpenEmail(params?.row?.id)} key={params?.row?.id}>
            {extractShortDate(params?.row?.createdAt)}
          </div>)
      },
      sortable: false
    },
  ]

  const deliveryColumn = {
    field: 'delivery',
    headerName: 'Status',
    width: 150,
    sortable: false,
    renderCell: ({ row }) => <DeliveryStatusBadge delivery={row.delivery} channel="email" />
  }

  const hasPendingDelivery = showDelivery && data.some((row) => isPendingDelivery('email', row.delivery?.status, row.createdAt))

  usePendingDeliveryPoll({ enabled: hasPendingDelivery, onPoll: () => getEmails({ silent: true }) })

  useEffect(() => {
    getEmails()
  }, [offset, searchQuery, activeTab])

  return (
    <div>
      <div className="flex justify-between px-4 py-5">
        <DebounceInput customClassName={'mr-5 b-1'} searchValue={searchQuery} setSearchValue={setSearchQuery}
          placeholder="Search..." />
        <CustomPagination totalPages={totalPages} setOffset={setOffset} offset={offset} />
      </div>
      <DataGrid
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          '.MuiDataGrid-virtualScroller': {
            minHeight: data?.length === 0 ? 300 : ''
          }
        }}
        rowHeight={75}

        columns={showDelivery ? [...columns, deliveryColumn] : columns}
        slots={{
          noRowsOverlay: notFound,
        }}
        columnVisibilityModel={{
          id: false
        }}
        hideFooterPagination={true}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnSelector
        rows={data ?? []}
        apiRef={apiRef}
      />
    </div>

  )
}

export default EmailTableList
